import React, { useEffect, useMemo, useState } from 'react';
import { SideBarStates, useSidebarDispatch } from '../../../context/SidebarContext';
import { BasePanelComponent } from './CommonComponents';
import { getClient } from '@graphql/graphql-client/graphClient';
import {
    Activity,
    CommentableObject,
    CommentedItemsQuery,
    PrivateDiscussion,
    useMarkReadAllCommentsMutation,
    useToggleReadCommentMutation,
} from '@graphql/generated-graphql';
import { SimpleSpinner } from '@components/common/loading/SimpleSpinner';
import { AnimatedSearchField } from '@components/form/animated/AnimatedSearchField';
import { SkipperAvatar } from '@skipper/ui';
import { QuizIconSVG } from '@components/common/svgs/QuizIconSVG';
import { AssignmentPaperSVG } from '@components/common/svgs/AssignmentPaperSVG';
import { useUserSession } from '@hooks/user/useDefaultSession';
import { useRouter } from 'next/router';
import { Tab } from '@headlessui/react';
import { TealTab } from '@components/base/elements/Tabs';
import { Toggle } from '@components/base';
import { useInvalidateQueries } from '@hooks/useInvalidateQueries';
import { truncate } from 'lodash';
import { useCommentedItemsData } from '@hooks/useCommentsData';
import { WithTooltip } from '@components/common/tooltip/SkipperTooltip';
import { useQueryClient } from 'react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faReply } from '@fortawesome/pro-solid-svg-icons/faReply';
import {prettyDateOrTime, prettyDateTime} from '@utils/dateFormatter';
import { CommentIconSVG } from '@components/common/svgs/CommentIconSVG';
import { useDebounce } from '@hooks/useDebounce';
import { transformString } from '@utils/transformString';
import dayjs from "dayjs";

export function CommentsPanel() {
    const router = useRouter();
    const invalidateQueries = useInvalidateQueries();
    const { toggle: sidebarToggle } = useSidebarDispatch();
    const [searchTerm, setSearchTerm] = useState<string>('');

    const debounceSearchKeyword = useDebounce(
        searchTerm.replace(/\s+/g, '').trim().toLowerCase(),
        500,
    );

    const commentedItems = useCommentedItemsData();
    const markReadAll = useMarkReadAllCommentsMutation(getClient('comments'), {
        onSuccess: () => invalidateQueries(['CommentedItems']),
    });

    const [currentUser] = useUserSession();
    const [onlyUnreadMap, setOnlyUnreadMap] = useState<Record<number, boolean>>({
        0: false,
        1: false,
    });
    const [tabIndex, setTabIndex] = useState(0);
    const onlyUnread = onlyUnreadMap[tabIndex];

    const filteredItems = useMemo(() => {
        if (commentedItems.all) {
            if (tabIndex == 0) {
                const filteredReceived = searchComments(
                    commentedItems.received,
                    debounceSearchKeyword,
                );
                return filteredReceived.filter((item) =>
                    onlyUnread ? item.item.comments.find((c) => !c.isViewed) : true,
                );
            } else if (tabIndex == 1 && commentedItems.watching.length > 0) {
                const filteredWatching = searchComments(
                    commentedItems.watching,
                    debounceSearchKeyword,
                );
                return filteredWatching.filter((item) =>
                    onlyUnread ? item.item.comments.find((c) => !c.isViewed) : true,
                );
            } else return searchComments(commentedItems.sent, debounceSearchKeyword);
        }
    }, [onlyUnread, tabIndex, commentedItems.all, debounceSearchKeyword]);

    useEffect(() => {
        setTabIndex(0)
        if (currentUser && router.query.comment) {
            sidebarToggle(SideBarStates.Comments);
        }
    }, [currentUser?.id]);

    return (
        <BasePanelComponent openState={SideBarStates.Comments}>
            <BasePanelComponent.BorderlessHeader
                title="Comments"
                content={
                    <div className="flex">
                        <AnimatedSearchField
                            value={searchTerm}
                            onChange={(e) => {
                                setSearchTerm(e.target.value);
                            }}
                            onClose={() => {
                                setSearchTerm('');
                            }}
                            size="small"
                            placeholder="Search"
                        />
                    </div>
                }></BasePanelComponent.BorderlessHeader>
            <BasePanelComponent.Body className="">
                <Tab.Group onChange={setTabIndex} selectedIndex={tabIndex} as={React.Fragment}>
                    <Tab.List className="border-b border-gray-200 flex mx-4 md:mx-[24px]">
                        <Tab className="focus:outline-none">
                            {({ selected }) => (
                                <TealTab selected={selected} text={'Received'}>
                                    <UnreadCommentItemsBadge items={commentedItems.received} />
                                </TealTab>
                            )}
                        </Tab>
                        {commentedItems.watching?.length > 0 && (
                            <Tab className="focus:outline-none">
                                {({ selected }) => (
                                    <TealTab selected={selected} text={'Watching'}>
                                        <UnreadCommentItemsBadge items={commentedItems.watching} />
                                    </TealTab>
                                )}
                            </Tab>
                        )}
                        <Tab className="focus:outline-none">
                            {({ selected }) => <TealTab selected={selected} text={'Sent'} />}
                        </Tab>
                    </Tab.List>
                </Tab.Group>
                {commentedItems.isLoading ? (
                    <SimpleSpinner className={'mx-auto my-8'} />
                ) : (
                    <>
                        {(tabIndex == 0 ||
                            (tabIndex == 1 && commentedItems.watching.length > 0)) && (
                            <div className={`flex items-center gap-3 my-3 w-full px-4`}>
                                <Toggle
                                    enabled={onlyUnread}
                                    setEnabled={(enabled) =>
                                        setOnlyUnreadMap((prev) => ({
                                            ...prev,
                                            [tabIndex]: enabled,
                                        }))
                                    }
                                />
                                <span className={`text-sm`}>Only show unread</span>
                                <button
                                    className={`text-teal-600 text-sm grow text-right`}
                                    onClick={() =>
                                        markReadAll.mutate({
                                            ids: filteredItems
                                                .filter((item) =>
                                                    item.item.comments.find((c) => !c.isViewed),
                                                )
                                                .map((item) => item.item.id),
                                        })
                                    }>
                                    Mark all as read
                                </button>
                            </div>
                        )}
                        {filteredItems?.length === 0 ? (
                            <div className="text-center text-gray-300 my-8">No comments</div>
                        ) : (
                            <div className="flex flex-col m-2 gap-2">
                                {filteredItems?.map((item, index) => {
                                    return (
                                        item.item.type === CommentableObject.PrivateDiscussion &&
                                        (item.item as PrivateDiscussion).targetItem.type ===
                                            CommentableObject.Activity && (
                                            <PrivateActivityDiscussion
                                                key={`${index}-${!!item.item.comments.find(
                                                    (c) => !c.isViewed,
                                                )}`}
                                                item={item}
                                                showReceived={
                                                    tabIndex == 0 ||
                                                    (tabIndex == 1 &&
                                                        commentedItems.watching.length > 0)
                                                }
                                            />
                                        )
                                    );
                                })}
                            </div>
                        )}
                    </>
                )}
            </BasePanelComponent.Body>
        </BasePanelComponent>
    );
}

function PrivateActivityDiscussion({
    item,
    showReceived,
}: {
    item: ReturnType<typeof useCommentedItemsData>['all'][number];
    showReceived: boolean;
}) {
    const router = useRouter();
    const queryClient = useQueryClient();
    const activity = (item.item as PrivateDiscussion).targetItem as Activity;
    const [currentUser] = useUserSession();
    // for Received view we show the last comment from another other user
    // for Sent view we show the last comment from the current user
    const comment = item.item.comments
        .filter((c) =>
            showReceived ? c.author.id != currentUser?.id : c.author.id == currentUser?.id,
        )
        .slice(-1)[0];
    const repliedAt = Array.from(item.item.comments)
        .reverse()
        .find(
            (c) =>
                c.author.id == currentUser?.id &&
                comment?.id &&
                comment.id < c.id &&
                !!item.studentEnrollments.find((se) => se.user.id == comment.author.id),
        )?.createdAt;
    const toggleRead = useToggleReadCommentMutation(getClient('comments'), {
        onSuccess: (data, args) => {
            // inline update query data for performance
            const currentData = queryClient.getQueryData('CommentedItems') as CommentedItemsQuery;
            currentData.commentedItems.forEach((item) => {
                item.comments.forEach((comment) => {
                    if (comment.id == args.commentId) comment.isViewed = !comment.isViewed;
                });
            });
            queryClient.setQueryData('CommentedItems', { ...currentData });
        },
    });

    const isUnread = useMemo(() => {
        return !!item.item.comments.find((c) => !c.isViewed);
    }, []);

    const handleToggleRead = (e) => {
        e.preventDefault();
        toggleRead.mutate({ commentId: comment.id });
    };

    return (
        comment &&
        item.url && (
            <div
                className={`flex gap-3 p-3 rounded-md cursor-pointer hover:bg-gray-100 ${
                    showReceived && isUnread ? '' : 'bg-gray-50'
                }`}
                onClick={(e) =>
                    !(e.target as HTMLElement).closest('button') && router.push(item.url)
                }>
                <div>
                    <SkipperAvatar
                        size="base"
                        imageUrl={comment.author.imageFileEntry?.url}
                        calendarColor={comment.author.calendarColor}
                        initials={comment.author.initials}
                    />
                    <div className="flex gap-0.5 justify-center my-2 items-center">
                        <span className="text-xs ">{item.item.comments.length}</span>
                        <CommentIconSVG active={true} className={'scale-[0.6]'} />
                    </div>
                </div>
                <div className={`flex-grow text-sm flex flex-col gap-1`}>
                    <div className={`flex gap-2 justify-between items-center`}>
                        <WithTooltip content={activity.unit.course.title}>
                            <div
                                className="rounded-full w-2.5 h-2.5"
                                style={{ backgroundColor: activity.unit.course.iconColor }}
                            />
                        </WithTooltip>
                        <div className={`flex gap-1 overflow-hidden text-ellipsis`}>
                            {activity.item.type == 'quiz' ? (
                                <QuizIconSVG className="w-5 h-5" />
                            ) : (
                                <AssignmentPaperSVG className="w-5 h-5" />
                            )}
                            {activity.item.title}
                        </div>
                        <WithTooltip content={dayjs(comment.createdAt).format('MMM D, YYYY h:mm A')}>
                            <span className="text-gray-400 text-xs mt-0.5">
                                {prettyDateOrTime(comment.createdAt)}
                            </span>
                        </WithTooltip>
                        <div className={'ml-auto flex gap-3 items-center'}>
                            {showReceived && (
                                <>
                                    {repliedAt && (
                                        <WithTooltip
                                            content={`Replied at ${prettyDateTime(repliedAt)}`}>
                                            <FontAwesomeIcon
                                                icon={faReply}
                                                className={'text-gray-300'}
                                            />
                                        </WithTooltip>
                                    )}
                                    <button onClick={handleToggleRead} className={'group p-2 -m-2'}>
                                        <WithTooltip
                                            content={isUnread ? 'Mark as read' : 'Mark as unread'}>
                                            <div
                                                className={`w-4 h-4 rounded-full ${
                                                    isUnread
                                                        ? 'bg-teal-500'
                                                        : 'bg-gray-200 border-teal-300 border-2 group-hover:bg-teal-500 group-hover:border-0'
                                                }`}
                                            />
                                        </WithTooltip>
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                    <div className={`text-sm`}>
                        <span className={`font-[500] mr-2`}>
                            {comment.author.fullName} commented
                            {item.studentEnrollments.length == 1 &&
                                comment.author.id != item.studentEnrollments[0].user.id &&
                                item.studentEnrollments[0].user.id != currentUser.id && (
                                    <span className="ml-1">
                                        to {item.studentEnrollments[0].user.fullName}
                                    </span>
                                )}
                        </span>
                    </div>
                    <div className={`text-gray-500 text-ellipsis overflow-hidden`}>
                        {truncate(comment.content, { length: 150 })}
                    </div>
                </div>
            </div>
        )
    );
}

function UnreadCommentItemsBadge({
    items,
}: {
    items: ReturnType<typeof useCommentedItemsData>['all'];
}) {
    const unreadComments = useMemo(() => {
        return items?.filter((item) => !!item.item.comments.find((c) => !c.isViewed)) || [];
    }, [items]);
    if (unreadComments.length > 0)
        return (
            <WithTooltip content={`New comments`}>
                <span
                    className={`w-5 h-5 rounded-full top-[-0.5rem] right-[-0.5rem] text-xs text-center justify-center flex items-center bg-teal-200 text-teal-600`}>
                    {unreadComments.length}
                </span>
            </WithTooltip>
        );
    else return <></>;
}

const searchComments = (comments: any['commentedItems'], searchTerm: string) => {
    return comments.filter(
        (c) =>
            transformString(c.item?.targetItem?.item?.title).includes(searchTerm) ||
            c.item?.comments?.find((comment) =>
                transformString(comment.author.fullName).includes(searchTerm),
            ) ||
            transformString(c.item?.targetItem?.unit?.course?.title).includes(searchTerm) ||
            c.studentEnrollments?.find((student) =>
                transformString(student.user.fullName).includes(searchTerm),
            ),
    );
};
